<script lang="ts">
	import { buildPageSeo, buildPersonSchema, renderJsonLd } from '$lib/seo';
	import { absoluteUrl, site } from '$lib/site';
	import type { PageData } from './$types';

	let { data }: { data: PageData } = $props();
	const seo = buildPageSeo({
		title: site.title,
		description: site.description,
		path: '/',
		keywords: ['software engineering', 'AI', 'product development', 'writing']
	});
	const personSchema = renderJsonLd(buildPersonSchema('/', site.description));
</script>

<svelte:head>
	<title>{seo.title}</title>
	<meta name="description" content={seo.description} />
	<meta name="author" content={site.author} />
	<meta name="keywords" content={seo.keywords} />
	<link rel="canonical" href={seo.canonical} />
	<link rel="alternate" type="application/rss+xml" title={`${site.name} RSS`} href={absoluteUrl('/rss.xml')} />
	<meta property="og:type" content={seo.type} />
	<meta property="og:title" content={seo.title} />
	<meta property="og:description" content={seo.description} />
	<meta property="og:url" content={seo.canonical} />
	<meta property="og:image" content={seo.image} />
	<meta property="og:site_name" content={site.name} />
	<meta name="twitter:card" content="summary_large_image" />
	<meta name="twitter:title" content={seo.title} />
	<meta name="twitter:description" content={seo.description} />
	<meta name="twitter:image" content={seo.image} />
	{@html personSchema}
</svelte:head>

<div class="page-shell">
	<header class="page-header">
		<div class="page-header__top">
			<p class="site-kicker">Jarle Mathiesen</p>
			<h1 class="site-title">Writing</h1>
		</div>
		<p class="intro">
			Writing on software engineering, AI, and the systems behind good products.
		</p>
	</header>

	<section class="about-note" aria-label="About Jarle Mathiesen">
			<img
			class="about-note__image"
			src={site.portraitImage}
			alt="Portrait of Jarle Mathiesen"
			loading="lazy"
			decoding="async"
		/>
		<div class="about-note__body">
			<p class="section-label">About</p>
			<p>
				I’m Jarle Mathiesen, a software engineer writing about AI, systems, and the craft of
				building good products.
			</p>
			<a class="about-more" href="/about">More</a>
		</div>
	</section>

	<section class="post-list" aria-label="Published posts">
		{#each data.posts as post}
			<article class="post-card">
				<div class="post-card__meta">
					<time datetime={post.date}>{post.formattedDate}</time>
					<ul class="tag-list" aria-label={`Tags for ${post.title}`}>
						{#each post.tags as tag}
							<li>{tag}</li>
						{/each}
					</ul>
				</div>

				<div class="post-card__body">
					<h2>
						<a href={`/writing/${post.slug}`}>{post.title}</a>
					</h2>
					<p>{post.description}</p>
				</div>
			</article>
		{/each}
	</section>

	<footer class="site-footer">
		<a href="/projects">Projects</a>
		<a href="/guides">Guides</a>
		<a href="/rss.xml">RSS</a>
	</footer>
</div>

<style>
	.page-shell {
		width: min(var(--measure-page), calc(100% - var(--space-2xl)));
		margin: 0 auto;
		min-height: 100svh;
		display: flex;
		flex-direction: column;
		padding: calc(var(--space-4xl) + var(--space-lg)) 0 var(--space-5xl);
	}

	.page-header {
		display: grid;
		gap: var(--space-md);
		padding-bottom: calc(var(--space-3xl) + var(--space-sm));
	}

	.page-header__top {
		display: grid;
		gap: var(--space-sm);
	}

	.site-kicker {
		font-family: var(--font-mono);
		font-size: var(--text-label);
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--color-soft);
	}

	.site-title {
		max-width: 12ch;
		font-size: clamp(3.6rem, 10vw, 6.8rem);
		color: var(--color-ink);
		line-height: 0.9;
		letter-spacing: -0.04em;
	}

	.intro {
		max-width: 32rem;
		font-size: var(--text-body);
		color: var(--color-muted);
		line-height: 1.62;
	}

	.about-note {
		display: grid;
		grid-template-columns: 9rem minmax(0, 1fr);
		gap: var(--space-xl);
		margin-bottom: calc(var(--space-3xl) + var(--space-sm));
		padding: 0;
	}

	.about-note__image {
		width: 9rem;
		height: 9rem;
		object-fit: cover;
		border-radius: 50%;
	}

	.about-note__body {
		display: grid;
		gap: var(--space-md);
		max-width: 30rem;
		align-content: start;
	}

	.section-label {
		font-size: var(--text-label);
		font-family: var(--font-mono);
		font-weight: 600;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--color-soft);
	}

	.about-note__body p {
		font-size: 1.08rem;
		line-height: 1.72;
		color: var(--color-ink-2);
	}

	.about-more {
		font-family: var(--font-mono);
		font-size: 0.82rem;
		letter-spacing: 0.08em;
		text-transform: uppercase;
	}

	.post-list {
		display: grid;
		gap: calc(var(--space-2xl) + var(--space-sm));
	}

	.post-card {
		display: grid;
		grid-template-columns: minmax(0, 9rem) minmax(0, 1fr);
		align-items: baseline;
		gap: var(--space-xl);
		padding: 0;
	}

	.post-card__meta {
		display: grid;
		align-content: start;
		gap: var(--space-sm);
		font-size: var(--text-sm);
		color: var(--color-soft);
	}

	.post-card__meta time {
		display: block;
		font-family: var(--font-mono);
		font-size: 0.78rem;
		line-height: 1;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--color-accent);
		font-weight: 600;
	}

	.tag-list {
		display: flex;
		flex-wrap: wrap;
		gap: var(--space-xs);
		line-height: 1.2;
	}

	.tag-list li {
		padding: 0;
		font-size: var(--text-meta);
		color: var(--color-faint);
	}

	.tag-list li::after {
		content: ',';
		margin-left: var(--space-2xs);
		color: var(--color-soft);
	}

	.tag-list li:last-child::after {
		content: '';
		margin: 0;
	}

	.post-card__body {
		display: grid;
		gap: var(--space-md);
	}

	.post-card__body h2 {
		max-width: var(--measure-post-title);
		font-size: 1.35rem;
		font-family: var(--font-display);
		font-weight: 500;
		line-height: 1.02;
		color: var(--color-ink);
		letter-spacing: -0.02em;
	}

	.post-card__body p {
		max-width: var(--measure-post-copy);
		font-size: var(--text-base);
		color: var(--color-muted);
		line-height: 1.68;
	}

	.post-card:first-child .post-card__body h2 {
		font-size: clamp(1.9rem, 4vw, 2.75rem);
		max-width: 16ch;
	}

	.post-card:first-child .post-card__body p {
		font-size: 1rem;
		max-width: 34rem;
	}

	.site-footer {
		display: flex;
		gap: var(--space-lg);
		margin-top: auto;
		padding-top: var(--space-3xl);
		font-family: var(--font-mono);
		font-size: 0.82rem;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--color-muted);
	}

	@media (max-width: 640px) {
		.page-shell {
			padding-top: var(--space-xl);
			padding-bottom: var(--space-4xl);
		}

		.site-title {
			font-size: clamp(2.8rem, 18vw, 4.5rem);
		}

		.about-note {
			grid-template-columns: 1fr;
			gap: var(--space-lg);
		}

		.post-card {
			grid-template-columns: 1fr;
			gap: var(--space-md);
		}

		.post-card:first-child .post-card__body h2 {
			font-size: 2rem;
		}
	}
</style>
